import { isBlankValue } from 'components/CreationForms/utils/fields';
import { useAuth } from 'context/auth';
import { checkIfLeadsEnabled } from 'crono-fe-common/utils';
import { captureException } from 'utils/exceptionUtils';

export type NativeMandatoryEntity = 'prospect' | 'account';

interface UseNativeMandatoryFieldsParams {
  entity: NativeMandatoryEntity;
  /** Contacts only: a Lead follows the Lead settings when the workspace has Leads. */
  createAsLead?: boolean;
}

/**
 * The standard fields the workspace makes mandatory when creating a contact,
 * lead or company (`otherSettings.default*MandatoryProperties`).
 */
export const useNativeMandatoryFields = ({
  entity,
  createAsLead = false,
}: UseNativeMandatoryFieldsParams) => {
  const { user } = useAuth();
  const settings = user?.otherSettings;

  let requiredFields: string[];
  if (entity === 'account') {
    requiredFields = settings?.defaultAccountMandatoryProperties ?? [];
  } else if (createAsLead && checkIfLeadsEnabled(user)) {
    requiredFields = settings?.defaultLeadMandatoryProperties ?? [];
  } else {
    requiredFields = settings?.defaultProspectMandatoryProperties ?? [];
  }

  const isRequired = (field: string) => requiredFields.includes(field);

  /**
   * Labels of the mandatory fields left blank. A configured field the form
   * doesn't have is reported to Sentry and skipped, as it can't be filled in.
   *
   * @param values the form's values, by field key
   * @param labels the label of each field the form shows, by field key
   */
  const getMissingFields = (
    values: Record<string, unknown>,
    labels: Record<string, string>,
  ) =>
    requiredFields
      .filter((field) => {
        if (!(field in labels)) {
          captureException(
            new Error(
              `New ${entity} form - Could not find field with key: ${field}`,
            ),
          );
          return false;
        }
        return isBlankValue(values[field]);
      })
      .map((field) => labels[field]);

  return { isRequired, getMissingFields };
};
